import React from "react"
import { Box, Typography } from "@mui/material"
import { useField } from "formik"
import { CreateUpdateBookingAttendee } from "../../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { DynamicFormField, DynamicFormFieldGroupWrap } from "../../form/DynamicFormField"
import { AccountingDimensionController } from "../../form/AccountingDimensionController"
import { SatelliteCanteensContainer } from "../satellites/SatelliteCanteensContainer"
import { BuyerPartiesController } from "../billReceiver/BuyerPartiesController"
import { DeliveryLocationAutocomplete } from "../DeliveryLocationAutocomplete"
import { ResponsibleAutocomplete } from "../../form/ResponsibleAutocomplete"
import { PaymentAutocomplete } from "../payment/PaymentAutocomplete"
import { StandardButton } from "../../../../buttons/StandardButton"
import { SelectedMainUserState } from "../../../createOrderTypes"
import { isAccountingDimensionKey } from "../../form/helpers"
import { DynamicDateTime } from "../../form/DynamicDateTime"
import { DeliveryNote } from "../deliveryNote/DeliveryNote"
import { Participants } from "../participants/Participants"
import { Recurrence } from "../meetingDetails/Recurrence"
import {
    DeliveryFulfillmentType,
    DynamicConfigField,
    DynamicConfigKey,
    MeetingServiceConfig,
} from "../../form/formTypes"

type FieldRendererProps = {
    config: DynamicConfigField
    groupTitle?: string
    meetingServiceConfiguration: MeetingServiceConfig
    user: SelectedMainUserState
    buttonLinkClass?: string
    deliveryFields: {
        deliveryLocations?: DynamicConfigField
        otherDeliveryLocation?: DynamicConfigField
    }
    showOtherLocationField: boolean
    setShowOtherLocationField: (v: boolean) => void
    isCreateBuyerParty?: boolean
}

function FieldRendererInner({
    config,
    groupTitle,
    meetingServiceConfiguration,
    user,
    buttonLinkClass,
    deliveryFields,
    showOtherLocationField,
    setShowOtherLocationField,
    isCreateBuyerParty,
}: FieldRendererProps) {
    const [{ value: deliveryType = "" }] = useField<DeliveryFulfillmentType>(DynamicConfigKey.DELIVERY_TYPE)
    const [{ value: registerParticipantsValue }] = useField<CreateUpdateBookingAttendee[]>(
        DynamicConfigKey.REGISTER_PARTICIPANTS,
    )

    if (!config.active) {
        return null
    }

    if (config.key === DynamicConfigKey.REGISTER_PARTICIPANTS) {
        const count = Array.isArray(registerParticipantsValue) ? registerParticipantsValue.length : 0

        return (
            <>
                {count > 0 && (
                    <LocalizedStrict
                        id={
                            count > 1
                                ? "create-order-participants-count-list"
                                : "create-order-participants-count-one-item"
                        }
                        vars={{ count }}
                    >
                        <Typography variant="body2">{count}</Typography>
                    </LocalizedStrict>
                )}
                <Participants meetingServiceConfiguration={meetingServiceConfiguration} />
            </>
        )
    }

    if (config.key === DynamicConfigKey.MERCHANT_ORDER_NOTE) {
        return <DeliveryNote meetingServiceConfiguration={meetingServiceConfiguration} />
    }

    if (config.key === DynamicConfigKey.DELIVERY_LOCATIONS) {
        if (deliveryType === DeliveryFulfillmentType.DELIVERY && deliveryFields.deliveryLocations) {
            return <DeliveryLocationAutocomplete userId={user.userId} config={config} />
        }
        return null
    }

    if (config.key === DynamicConfigKey.OTHER_DELIVERY_LOCATION) {
        if (deliveryType === DeliveryFulfillmentType.DELIVERY && deliveryFields.otherDeliveryLocation) {
            return (
                <>
                    {!showOtherLocationField ? (
                        <Box>
                            <LocalizedStrict id="create-order-drawer-other-location">
                                <StandardButton
                                    type="button"
                                    color="blue"
                                    variant="text"
                                    size="small"
                                    fullWidth={false}
                                    onClick={() => setShowOtherLocationField(true)}
                                    updateDefaultClasses={buttonLinkClass}
                                >
                                    + Other location
                                </StandardButton>
                            </LocalizedStrict>
                        </Box>
                    ) : (
                        <DynamicFormField config={config} />
                    )}
                </>
            )
        }
        return null
    }

    if (config.key === DynamicConfigKey.MEETING_RESPONSIBLE) {
        return (
            <DynamicFormFieldGroupWrap config={config}>
                <ResponsibleAutocomplete
                    name={DynamicConfigKey.MEETING_RESPONSIBLE}
                    label={config.label || config.description}
                    user={user}
                />
            </DynamicFormFieldGroupWrap>
        )
    }

    if (config.key === DynamicConfigKey.RECURRENCY) {
        return <Recurrence title={groupTitle} description={config.description} />
    }

    if (config.key === DynamicConfigKey.CREATE_BUYER_PARTY) {
        return null
    }
    if (config.key === DynamicConfigKey.BUYER_PARTIES) {
        return <BuyerPartiesController config={config} user={user} isCreateBuyerParty={isCreateBuyerParty} />
    }

    if (config.key === DynamicConfigKey.MEETING_START_TIME || config.key === DynamicConfigKey.MEETING_END_TIME) {
        return <DynamicDateTime config={config} />
    }

    // if (config.key === DynamicConfigKey.PAYMENT_METHOD) {
    //     return <PaymentAutocomplete config={config} />
    // }

    if (isAccountingDimensionKey(config.key)) return <AccountingDimensionController config={config} user={user} />

    if (config.key === DynamicConfigKey.SATELLITES) {
        return <SatelliteCanteensContainer config={config} user={user} />
    }

    return <DynamicFormField config={config} user={user} />
}

export const FieldRenderer = React.memo(FieldRendererInner)
